import { Location } from "@/interfaces/location";
import {
  LocationTraceDetails,
  TraceabilityConfig
} from "@/interfaces/traceability";
import { policyList } from "@/internal";
import { StateTraceabilityHeaders } from "@/metadata/traceability";
import { locationsService } from "@/services/locations.service";
import { traceabilityService } from "@/services/traceability.service";
import {
  BooleanCheck,
  TableAction,
  TableComponent,
  TablePagination,
  TablePaginationComponent
} from "helix-vue-components";
import cloneDeep from "lodash/cloneDeep";
import union from "lodash/union";
import { Component, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";
import Template from "./StateTraceability.template.vue";

@Component({
  mixins: [Template],
  components: {
    TableComponent,
    TablePaginationComponent
  }
})
export default class StateTraceabilityComponent extends Vue {
  @Getter("hasPermission", { namespace: "authModule" })
  public hasPermission!: BooleanCheck;
  public loading = false;
  public locations: Location[] = [];
  public traceabilityConfig: TraceabilityConfig[] = [];
  public traceValues!: LocationTraceDetails[];
  public biotrackConfigValues: LocationTraceDetails[] = [];
  public metrcConfigValues: LocationTraceDetails[] = [];
  public configValues: any[] = [];
  public locationsUniq: number[] = [];
  public headers = StateTraceabilityHeaders;
  public colors = ["white", "#f2f2f2", "#f2f2f2"];
  public tableActions: TableAction[] = [];
  public pagination: TablePagination | null = null;
  public modifyFlag: boolean = false;

  public mounted() {
    this.modifyFlag = this.hasPermission(
      policyList.modifyStateTraceabilityIntegrations
    );
    this.tableActions = [
      {
        icon: this.modifyFlag ? "fal fa-pen" : "fal fa-eye",
        class: "table__actions",
        action: this.editIntegration,
        tooltip: this.modifyFlag
          ? this.$t("traceability.edit_integration").toString()
          : this.$t("traceability.view_integration").toString(),
        visibleCondition: arg => {
          return (
            this.hasPermission(policyList.viewStateTraceabilityIntegrations) &&
            arg.item.value !== "None"
          );
        }
      }
    ];

    this.buildTable();
  }

  public editIntegration(item: TraceabilityConfig) {
    this.$router.push({
      name: this.modifyFlag
        ? "trace-integration-edit"
        : "trace-integration-view",
      params: { id: String(item.location_id) }
    });
  }

  public onChangePage(pagination: TablePagination) {
    this.buildTable({
      per_page: pagination.itemsPerPage,
      page: pagination.currentPage
    });
  }

  protected async buildTable(
    pagination: { per_page: number; page: number } | null = null
  ) {
    this.loading = true;
    this.traceabilityConfig = await traceabilityService.getCurrentTraceability(
      pagination || {}
    );
    this.pagination = cloneDeep(await traceabilityService.getPagination());
    const locationsUniq = this.traceabilityConfig.reduce((acc: number[], t) => {
      if (
        !acc.includes(t.location_id) &&
        !this.locations.find(l => l.id === t.location_id)
      ) {
        acc.push(t.location_id);
      }
      return acc;
    }, []);
    if (locationsUniq.length) {
      this.locationsUniq = union(this.locationsUniq, locationsUniq);
      [
        this.locations,
        this.biotrackConfigValues,
        this.metrcConfigValues
      ] = await Promise.all([
        locationsService.getLocations({
          "q[id_is_in]": locationsUniq
        }),
        traceabilityService.getBiotrackLocationValues(this.locationsUniq),
        traceabilityService.getMetrcLocationValues(this.locationsUniq)
      ]);
      this.traceValues = union(
        this.biotrackConfigValues,
        this.metrcConfigValues
      );
    }

    this.traceabilityConfig = this.traceabilityConfig.map(t => {
      const traceabilityType =
        t.value === "Biotrack Traceability"
          ? "biotrack_traceability_config"
          : t.value === "Metrc"
          ? "traceability_config"
          : null;
      const location = this.locations.find(l => l.id === t.location_id);
      t.location = location;
      const config = this.traceValues.find(
        c =>
          c.location_id === t.location_id && c.schema!.name === traceabilityType
      );
      t.config = config || null;
      if (t.config) {
        t.config.value =
          typeof config!.value === "string"
            ? JSON.parse(config!.value)
            : config!.value;
      }
      t.config_status =
        (config && config.value.enabled && "Enabled") || "Disabled";
      return t;
    });
    this.loading = false;
  }
}
